/**
 * Hook: useUsedWordTracking
 *
 * Integrates used-word tracking with Lexical editor.
 * - Registers text content listener on editor
 * - Debounces updates to 300ms
 * - Offloads analysis to Web Worker
 * - Updates Zustand store with results
 */

import { useEffect, useRef, useMemo, useCallback } from 'react';
import type { LexicalEditor } from 'lexical';
import { useWordCollectionStore } from '../store/wordCollectionStore';
import type { UsedTrackingConfig } from '../utils/wordCollection/usedTracking';
import type { AnalyzeMessage, ResultMessage } from '../workers/usedTrackingWorker';
import type { Word } from '../types/wordCollection';

const DEBOUNCE_MS = 300;

// Worker factory function - isolate import.meta usage
function createUsedTrackingWorker(): Worker | null {
  // Skip worker creation in test environment
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    return null;
  }

  try {
    // Vite worker import pattern (only works in browser/Vite environment)
    return new Worker(
      new URL('../workers/usedTrackingWorker.ts', import.meta.url),
      { type: 'module' }
    );
  } catch (error) {
    console.warn('Failed to create used word tracking worker:', error);
    return null;
  }
}

export function useUsedWordTracking(
  editor: LexicalEditor | null,
  collectedWords: Word[],
  config?: Partial<UsedTrackingConfig>
) {
  const setUsedWords = useWordCollectionStore(state => state.setUsedWords);
  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Extract word text from Word objects (memoized to prevent re-analysis on unrelated changes)
  const wordList = useMemo(
    () => collectedWords.map(w => w.word),
    [collectedWords]
  );

  // Initialize worker once
  useEffect(() => {
    workerRef.current = createUsedTrackingWorker();

    // If worker creation failed, tracking will be disabled (graceful degradation)
    if (!workerRef.current) {
      return;
    }

    // Handle worker results
    workerRef.current.onmessage = (event: MessageEvent<ResultMessage>) => {
      if (event.data.type === 'RESULT') {
        // Convert array back to Set
        const usedWordsSet = new Set(event.data.usedWords);
        setUsedWords(usedWordsSet);
      }
    };

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [setUsedWords]);

  // Debounced analysis function
  const analyzeText = useCallback((text: string) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce analysis
    timeoutRef.current = setTimeout(() => {
      if (workerRef.current) {
        const message: AnalyzeMessage = {
          type: 'ANALYZE',
          lyrics: text,
          collectedWords: wordList,
          config,
        };
        workerRef.current.postMessage(message);
      }
    }, DEBOUNCE_MS);
  }, [wordList, config]);

  // Register text content listener
  useEffect(() => {
    if (!editor) return;

    // Register listener that fires on text content changes
    const unregister = editor.registerTextContentListener((textContent) => {
      analyzeText(textContent);
    });

    // Run initial analysis
    editor.getEditorState().read(() => {
      const initialText = editor.getEditorState().read(() => {
        return editor.getRootElement()?.textContent || '';
      });
      analyzeText(initialText);
    });

    return () => {
      unregister();
      // Clear any pending timeout on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [editor, analyzeText]);
}
